import { useMutation } from '@apollo/client';
import { useI18n } from '@i18n';
import { Box, Button } from '@mui/material';
import { DataCollectionAreaConsumptionDraftFragment } from '@predium/client-graphql';
import { consumption_source_type_enum, energy_source_type_enum } from '@predium/enums';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toIsoDateString } from '@/utils/formatTime';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { SnackbarTimeouts } from '../../components/NotistackProvider';
import PreDialog, { PreDialogBody } from '../../components/presentations/PreDialog/PreDialog';
import { WarningModal } from '../../components/WarningModal';
import {
  CREATE_MANUAL_CONSUMPTION,
  DELETE_CONSUMPTION_DRAFT,
  INSERT_SINGLE_CONSUMPTION_FROM_DRAFT,
} from '../../graphql/DataCollection.mutations';
import {
  GET_BUILDING,
  GET_CONSUMPTION_DRAFTS,
  GET_CONSUMPTION_SUMMARIES,
  GET_CONSUMPTIONS,
} from '../../graphql/DataCollection.queries';
import usePosthogTracking from '../../hooks/usePosthogTracking';
import { PATHS } from '../../routes';
import { BuildingTabEnum } from '../../routes/search_params/searchParamsEnums';
import AreaConsumptionInvoiceDraft from '../../sections/DataCollection/Building/Consumption/AreaConsumptionInvoiceDraft';
import { ConsumptionTabNamesEnum } from '../../sections/DataCollection/Building/Consumption/AreaConsumptionListTabs';
import AreaSingleConsumptionDraftForm, {
  OnCreateSingleConsumption,
  SingleConsumptionPrefill,
} from '../../sections/DataCollection/Building/Drafts/AreaSingleConsumptionDraftForm';

type Props = {
  buildingId: number;
  isOpen: boolean;
  consumptionSourceType: consumption_source_type_enum;
  prefill?: SingleConsumptionPrefill;
  onClose: () => void;
  consumptionDraftData: DataCollectionAreaConsumptionDraftFragment | null;
  setConsumptionDraftData: React.Dispatch<React.SetStateAction<DataCollectionAreaConsumptionDraftFragment | null>>;
};

export default function DataCollectionSingleConsumptionDraftModal({
  buildingId,
  consumptionSourceType,
  prefill,
  isOpen,
  onClose,
  consumptionDraftData,
  setConsumptionDraftData,
}: Props) {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { trackEvent } = usePosthogTracking();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isChildFormDirty, setIsChildFormDirty] = useState(false);
  const [isOpenWarningDialog, setIsOpenWarningDialog] = useState(false);

  const [deleteConsumptionDraftMutation] = useMutation(DELETE_CONSUMPTION_DRAFT, {
    variables: {
      consumptionDraftId: consumptionDraftData?.id!,
    },
    update: (cache, { data }) => {
      if (data?.delete_consumption_draft_by_pk) {
        const normalizedId = cache.identify({
          id: data.delete_consumption_draft_by_pk.id,
          __typename: data.delete_consumption_draft_by_pk.__typename,
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      }
    },
    onCompleted: (data, options) => {
      if (data.delete_consumption_draft_by_pk && !options?.context?.skipNotification) {
        enqueueSnackbar(t('DataCollectionAreaConsumptionDraft_DeleteConsumptionDraft-success'), {
          variant: 'success',
          autoHideDuration: SnackbarTimeouts.Success,
        });
      }
      setDeleteModalOpen(false);
      onClose();
    },
    onError: () =>
      enqueueSnackbar(t('DataCollectionAreaConsumptionDraft_DeleteConsumptionDraft-error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      }),
  });

  const consumptionRefetchQueries = [
    { query: GET_BUILDING, variables: { buildingId } },
    { query: GET_CONSUMPTION_SUMMARIES, variables: { buildingId, year: new Date().getFullYear() } },
    { query: GET_CONSUMPTIONS, variables: { buildingId } },
    { query: GET_CONSUMPTION_DRAFTS, variables: { buildingId } },
  ];

  const consumptionSuccessSnackbar = () =>
    enqueueSnackbar(t('DataCollectionAreaConsumptionDraft_CreateConsumption-success'), {
      variant: 'success',
      autoHideDuration: SnackbarTimeouts.Success,
    });

  const consumptionErrorSnackbar = () =>
    enqueueSnackbar(t('DataCollectionAreaConsumptionDraft_CreateConsumption-error'), {
      variant: 'error',
      autoHideDuration: SnackbarTimeouts.Error,
    });

  const [createManualConsumptionMutation] = useMutation(CREATE_MANUAL_CONSUMPTION, {
    refetchQueries: consumptionRefetchQueries,
    onError: consumptionErrorSnackbar,
    onCompleted: consumptionSuccessSnackbar,
  });

  const [createConsumptionFromDraftMutation] = useMutation(INSERT_SINGLE_CONSUMPTION_FROM_DRAFT, {
    refetchQueries: consumptionRefetchQueries,
    onError: consumptionErrorSnackbar,
    onCompleted: consumptionSuccessSnackbar,
  });

  const handleCreateSingleConsumption: OnCreateSingleConsumption = async (consumptionData) => {
    if (consumptionData.type === 'manual') {
      const { type: _, waste, sourceTypeId, ...consumptionInput } = consumptionData;

      const { data } = await createManualConsumptionMutation({
        variables: { ...consumptionInput, buildingId, wasteDetails: waste ?? undefined, sourceTypeId },
      });

      if (!data?.createManualConsumption) {
        console.error('Failed to create consumption');
        return;
      }

      trackEvent('CONSUMPTION_MANUAL_ENTERED', {
        building_id: buildingId,
        consumption_id: data.createManualConsumption.id,
        energy_source: consumptionInput.energySourceTypeId ?? energy_source_type_enum.UNSPECIFIED,
        period_start: toIsoDateString(consumptionInput.from),
        period_end: toIsoDateString(consumptionInput.to),
      });
    } else {
      const { data } = await createConsumptionFromDraftMutation({
        variables: { ...consumptionData.variables, buildingId },
      });

      if (!data?.createConsumptionFromDraft) {
        console.error('Failed to create consumption');
        return;
      }

      trackEvent('CONSUMPTION_MANUAL_ENTERED', {
        building_id: buildingId,
        consumption_id: data.createConsumptionFromDraft.id,
        energy_source: consumptionData.variables.energySourceTypeId ?? energy_source_type_enum.UNSPECIFIED,
        period_start: toIsoDateString(consumptionData.variables.from),
        period_end: toIsoDateString(consumptionData.variables.to),
      });
    }

    onClose();
  };

  return (
    <>
      <PreDialog
        open={Boolean(isOpen)}
        onClose={() => {
          const hasDraftWorkflow =
            consumptionSourceType !== consumption_source_type_enum.MANUAL_ENTRY &&
            consumptionSourceType !== consumption_source_type_enum.ESTIMATION;

          if (isChildFormDirty && hasDraftWorkflow) {
            setIsOpenWarningDialog(true);
          } else {
            onClose();
          }
        }}
        type="definedByChildren"
        maxWidth="sm"
        fullWidth
      >
        <AreaSingleConsumptionDraftForm
          buildingId={buildingId}
          prefill={prefill}
          consumptionDraftData={consumptionDraftData}
          setConsumptionDraftData={setConsumptionDraftData}
          sourceTypeId={consumptionSourceType}
          onCreateSingleConsumption={handleCreateSingleConsumption}
          setIsChildFormDirty={setIsChildFormDirty}
          renderChildren={(submitForm) => (
            <>
              <PreDialogBody
                dialogtitle={
                  consumptionSourceType === consumption_source_type_enum.ESTIMATION
                    ? t('DataCollection_ConsumptionDraftModalTitle-estimation')
                    : t('PageName_DataCollection_ConsumptionDraft')
                }
                content={<AreaConsumptionInvoiceDraft isSingleConsumption />}
                // full height - dialog top margin - dialog bottom margin - header - actions
                scrollBarContentHeight="calc(100vh - 32px - 32px - 76px - 84px)"
                actions={
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      variant="text"
                      color="error"
                      disabled={!consumptionDraftData}
                      onClick={() => setDeleteModalOpen(true)}
                    >
                      {t('General_Delete')}
                    </Button>

                    <Box>
                      {consumptionSourceType !== consumption_source_type_enum.MANUAL_ENTRY &&
                        consumptionSourceType !== consumption_source_type_enum.ESTIMATION && (
                          <Button
                            variant="outlined"
                            onClick={() => {
                              submitForm().then(() => {
                                onClose();
                                navigate(
                                  PATHS.dataCollection.building(
                                    { id: buildingId },
                                    {
                                      tab: BuildingTabEnum.consumption,
                                      consumptionsTab: ConsumptionTabNamesEnum.consumption_drafts,
                                    },
                                  ),
                                );
                              });
                            }}
                          >
                            {consumptionDraftData ? t('General_SaveDraft') : t('General_SaveAsDraft')}
                          </Button>
                        )}
                      <Button sx={{ marginLeft: 1 }} variant="contained" type="submit">
                        {t('General_Create')}
                      </Button>
                    </Box>
                  </Box>
                }
              />
              {isOpenWarningDialog ? (
                <WarningModal
                  title={t('General_UnsavedChanges')}
                  open={isOpenWarningDialog}
                  description={t('DataCollection_UnsavedChangesInDraft-Message')}
                  onAcknowledge={() => {
                    submitForm().then(() => {
                      setIsOpenWarningDialog(false);
                      onClose();
                    });
                  }}
                  onClose={() => {
                    setIsOpenWarningDialog(false);
                    onClose();
                  }}
                  buttonText={t('General_Save')}
                  cancelText={t('General_Leave')}
                />
              ) : null}
            </>
          )}
        />
      </PreDialog>

      {isOpen ? (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          title={t('DataCollection_DeleteModalTitle-ConsumptionDraft')}
          description={t('DataCollection_DeleteModalDescription-ConsumptionDraft')}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={async () => {
            deleteConsumptionDraftMutation();
          }}
        />
      ) : null}
    </>
  );
}
